import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  FormGroupDirective,
  ValidatorFn,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { ErrorTextObj } from '@clux-models';
import {
  NgbDateParserFormatter,
  NgbDatepicker,
  NgbDatepickerI18n,
  NgbDateStruct,
  NgbInputDatepicker,
} from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { map, tap } from 'rxjs';
import { CommonConstants } from 'src/app/shared/constants/common.constant';
import { maxDate, minDate } from 'src/app/shared/services/form.validator';
import { CustomDatepickerI18n } from 'src/app/shared/utils/custom-datepicker-i18n';
import { NgbDateCustomParserFormatter } from 'src/app/shared/utils/dateformater';
import { Dates } from 'src/app/shared/utils/dates';
import { InvalidErrorStateMatcher } from 'src/app/shared/utils/errorstate-matcher';
import { MatFormField, MatLabel, MatInput, MatError } from '@angular/material/input';
import { DateMaskDirective } from '../../../../../../shared/directives/date-mask.directive';
import { AsyncPipe } from '@angular/common';
import { DATEPICKER_CONTAINER, DatepickerContainer } from '@app/shared/utils/datepicker.util';

@Component({
  selector: 'app-date-field',
  templateUrl: './date-field.component.html',
  styleUrls: ['./date-field.component.scss'],
  providers: [{ provide: NgbDatepickerI18n, useClass: CustomDatepickerI18n }, { provide: NgbDateParserFormatter, useClass: NgbDateCustomParserFormatter }, NgbDateCustomParserFormatter],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [FormsModule, ReactiveFormsModule, MatFormField, MatLabel, MatInput, NgbInputDatepicker, DateMaskDirective, MatError, AsyncPipe],
})
export class DateFieldComponent implements OnInit, OnDestroy {
  private rootFormGroup = inject(FormGroupDirective);
  private ngbDateFormatter = inject(NgbDateCustomParserFormatter);

  @Input() public label = 'Date of birth';
  @Input() public showLabelAfterInput = true;
  @Input() public controlName = 'dateOfBirth';
  @Input() public errorState: InvalidErrorStateMatcher;
  @Input() public datacy = 'dob-input';
  @Input() public maxDate: string;
  @Input() public minDate = Dates.MIN_DATE;
  @Input() public dynamicMinDate: Observable<string> = of(null);
  @Input() public colClass: string;
  @Input() public errorTextObj: ErrorTextObj;
  @Input() public displayMonths?: number;
  /**
   * Set to `true` when this date field is rendered inside a modal (Angular Material
   * `MatDialog`). Controls where the ngb-datepicker calendar popup is attached:
   *   - `false` (default, on a normal page): the popup attaches to `<body>` so it can
   *     escape clipping/overflow parents.
   *   - `true` (inside a dialog): the popup renders inline instead. A CDK v22 dialog
   *     renders in the browser's top layer, which sits above all normal-DOM content
   *     regardless of z-index — so a `<body>`-attached popup would render BEHIND the
   *     dialog and be invisible. Rendering inline keeps it inside the dialog's layer.
   * See datepicker.util.ts for the full explanation.
   */
  @Input() public isRenderedInModal = false;
  @Input() private customValidators: ValidatorFn[] = [];

  public get datepickerContainer(): DatepickerContainer {
    return this.isRenderedInModal ? DATEPICKER_CONTAINER.modal : DATEPICKER_CONTAINER.page;
  }
  @ViewChild('d') public dateInput: NgbDatepicker;
  private readonly subscriptionTracker$ = new Subscription();
  public form: UntypedFormGroup;
  public dateControl: UntypedFormControl = new UntypedFormControl();
  public errorTypes$ = new BehaviorSubject<string[]>([]);
  public dynamicMinDate$: Observable<boolean>;

  public get ngbMinDate(): NgbDateStruct {
    return this.ngbDateFormatter.parse(this.minDate);
  }

  public get ngbMaxDate(): NgbDateStruct {
    return this.ngbDateFormatter.parse(this.maxDate);
  }

  public ngOnInit(): void {
    this.dynamicMinDate$ = this.dynamicMinDate.pipe(
      tap((dynamicMinDate) => this.setupDates(dynamicMinDate)),
      map(() => true)
    );
  }

  private setupDates(dynamicMinDate: string): void {
    if (dynamicMinDate) {
      this.minDate = dynamicMinDate;
    }

    this.form = this.rootFormGroup.control;
    this.dateControl = this.form.get(this.controlName) as UntypedFormControl;

    if (this.controlName === 'dateOfBirth') {
      this.maxDate = Dates.today();
    }

    if (!this.maxDate) {
      this.maxDate = Dates.addDays(Dates.today(), this.tenYears());
    }

    const dateMaxString = Dates.formatTo(this.maxDate, 'MM/DD/YYYY');
    const dateMinString = Dates.formatTo(this.minDate, 'MM/DD/YYYY');

    const validators = [...this.customValidators, Validators.required, minDate(this.minDate, Dates.DATE_FORMAT)];
    if (this.maxDate) {
      validators.push(maxDate(this.maxDate, Dates.DATE_FORMAT));
    }
    this.dateControl.setValidators(validators);
    setTimeout(() => {
      this.dateControl.updateValueAndValidity();
      if (this.dateControl.value?.length === 10) {
        this.dateInput.writeValue(this.ngbDateFormatter.parse(this.dateControl.value));
      }
    });

    this.errorTextObj = {
      required: `${this.label} is required.`,
      afterMaxDate: `Date cannot be after ${dateMaxString}`,
      dateInvalidMax: `Date cannot be after ${dateMaxString}`,
      dateInvalid: CommonConstants.errorCodes.invalidDateError,
      beforeMinDate: `Date cannot be before ${dateMinString}`,
      dateInvalidMin: `Date cannot be before ${dateMinString}`,
      ...this.errorTextObj,
    };

    this.dateControl.valueChanges.subscribe(() => {
      this.checkErrors();
    });
  }

  private tenYears(): number {
    return 3652.5;
  }

  public ngOnDestroy(): void {
    if (this.subscriptionTracker$) {
      this.subscriptionTracker$.unsubscribe();
    }
  }

  public onDateChange(value: NgbDateStruct | Event): void {
    let date = '';
    let ngbDate: NgbDateStruct;

    const dateGuard = (v: NgbDateStruct | Event): v is NgbDateStruct => !!v && !!(v as unknown as Record<string, unknown>)['year'];
    const eventGuard = (v: NgbDateStruct | Event): v is Event => !!v && !!(v as unknown as Record<string, unknown>)['target'];
    if (dateGuard(value)) {
      date = this.ngbDateFormatter.format(value);
      ngbDate = value;
    } else if (eventGuard(value)) {
      // @ts-expect-error: value.target exists at runtime but is not typed on the union
      date = value.target.value;
      ngbDate = this.ngbDateFormatter.parse(date);
    }

    if (date.length === 10) {
      this.dateControl.setValue(Dates.format(date));
      this.dateInput.writeValue(ngbDate);
    } else {
      this.dateControl.setValue(date);
    }
    this.dateControl.markAsTouched();
    this.dateControl.markAsDirty();
    this.checkErrors();
  }

  public checkErrors(): void {
    if (!this.dateControl.touched && !this.dateControl.dirty) {
      return; // Prevent error display if the user hasn't interacted with the field
    }

    if (this.dateControl.invalid) {
      if (!this.dateControl.value || this.dateControl.errors?.required) {
        this.errorTypes$.next(['required']);
      } else if (this.dateControl.errors?.dateInvalid) {
        this.errorTypes$.next(['dateInvalid']);
      } else {
        this.errorTypes$.next(Object.keys(this.dateControl.errors));
      }
    } else {
      this.errorTypes$.next([]);
    }
  }
}
